import React, { useEffect, useRef, useState } from "react";
import { ComboBox, Item } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { useTranslation } from "react-i18next";
import { useDependency } from "../../../../../../contexts/DependencyProvider";
import { ListCurrencyRequest } from "../../../../../../services/soap/global/requests/ListCurrencyRequest";

export interface Character {
  id: string;
  code: string;
  name: string;
  country: string;
  countryName: string;
}

interface IInvoiceCurrencyDropDownProps {
  selectedCurrency: string;
  onChange: (data: Character | null) => void;
  isRequired?: any;
  errorMessage?: any;
  validationState?: any;
  width?: any
  label?: React.ReactNode
}

const DEFAULT_CURRENCY_CODE = "CZK";

const InvoiceCurrencyDropDown: React.FC<IInvoiceCurrencyDropDownProps> = ({
  selectedCurrency,
  onChange,
  isRequired,
  errorMessage,
  validationState,
  width,
  label
}) => {
  const { store, globalService } = useDependency();
  const { t } = useTranslation("invoice");
  const _t = (key: string, defaultValue?: string) =>
    t(`add_update_invoice.${key}`, {
      ns: "invoice",
      ...(defaultValue !== undefined ? { defaultValue } : {}),
    });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const isUserEditingRef = useRef(false);
  const didInitialSelectRef = useRef(false);

  const list = useAsyncList<Character>({
    async load({ cursor, filterText }) {
      const offset = cursor ? Number(cursor) : 0;
      const limit = 50;

      try {
        const request = new ListCurrencyRequest(
          store.Server,
          store.SessionId,
          limit,
          offset
        );
        const response = await globalService.listCurrency(request);

        let items: Character[] =
          response?.ROWS?.map((r: any) => r.ITEM) || [];
        if (!Array.isArray(items)) return { items: [] };

        // ✅ search ONLY by currency code
        const ft = (filterText ?? "").trim().toLowerCase();
        if (ft) {
          items = items.filter((c) =>
            (c.code ?? "").toLowerCase().includes(ft)
          );
        }

        return {
          items,
          cursor: items.length === limit ? String(offset + limit) : undefined,
        };
      } catch (e) {
        console.error("Error fetching currencies:", e);
        return { items: [] };
      }
    },
  });

  useEffect(() => {
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ initial select: CZK → else fallback to 0th (ONCE)
  useEffect(() => {
    if (
      didInitialSelectRef.current ||
      list.items.length === 0 ||
      selectedCurrency
    ) {
      return;
    }

    didInitialSelectRef.current = true;

    // try to find CZK
    const czk = list.items.find(
      (x) => x.code?.toUpperCase() === DEFAULT_CURRENCY_CODE
    );

    const initial = czk ?? list.items[0];

    setSelectedKey(String(initial.code));
    setInputValue(initial.code ?? "");
    onChange(initial);
  }, [list.items, selectedCurrency, onChange]);

  // ✅ sync from parent ONLY when user not typing
  useEffect(() => {
    if (isUserEditingRef.current) return;

    if (!selectedCurrency) {
      setSelectedKey(null);
      setInputValue("");
      return;
    }

    const selected = list.items.find(
      (x) => String(x.code) === String(selectedCurrency)
    );
    setSelectedKey(selected ? String(selectedCurrency) : null);
    setInputValue(selected?.code ?? String(selectedCurrency));
  }, [selectedCurrency, list.items]);

  const handleInputChange = (val: string) => {
    isUserEditingRef.current = true;

    setInputValue(val);
    list.setFilterText(val);

    // allow backspace without reinserting selection
    if (selectedKey) {
      const selected = list.items.find(
        (x) => String(x.code) === String(selectedKey)
      );
      const selectedLabel = selected?.code ?? "";
      if (val !== selectedLabel) {
        setSelectedKey(null);
      }
    }

    // cleared → empty dropdown
    if (!val.trim()) {
      setSelectedKey(null);
      list.setFilterText("");
      onChange(null);
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    isUserEditingRef.current = false;

    const idStr = key ? String(key) : null;
    setSelectedKey(idStr);

    if (!idStr) return;

    const selected = list.items.find(
      (x) => String(x.code) === idStr
    );
    if (selected) {
      setInputValue(selected.code ?? "");
      onChange(selected);
      list.setFilterText("");
    }
  };

  return (
    <ComboBox
      label={label ? label : _t("currency", "Currency")}
      aria-label={typeof label === "string" ? label : _t("currency", "Currency")}
      items={list.items}
      selectedKey={selectedKey}
      inputValue={inputValue}
      loadingState={list.loadingState}
      width={width ? width : '100%'}
      isRequired={isRequired}
      errorMessage={errorMessage}
      validationState={validationState}
      onFocus={() => {
        isUserEditingRef.current = true;
        list.setFilterText("");
      }}
      onBlur={() => {
        isUserEditingRef.current = false;
      }}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
    >
      {(item) => (
        <Item key={item.code} textValue={item.code}>
          {item.code}
        </Item>
      )}
    </ComboBox>
  );
};

export default InvoiceCurrencyDropDown;
